import React, { forwardRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  IconButton,
  Slide,
  useTheme,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { tokens } from "../../theme";

// Transition for the Dialog (slides up/down)
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ViewMoreDialog = ({ open, onClose, rowData }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Local state to preserve data during the exit animation
  const [localData, setLocalData] = useState(rowData);

  useEffect(() => {
    if (rowData) {
      setLocalData(rowData);
    }
  }, [rowData]);

  if (!localData) return null;

  // Currency formatter
  const formatCurrency = (value) => {
    if (value == null) return "N/A";
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Date formatter
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  // Payment Status Badge Color Resolver
  const getStatusColor = (status) => {
    switch (status) {
      case "FULLY_PAID":
        return { bg: colors.greenAccent[700], color: "#ffffff" };
      case "PARTIAL_PAYMENT":
        return { bg: colors.yellowAccent[600], color: "#ffffff" };
      case "PENDING_PAYMENT":
        return { bg: colors.redAccent[600], color: "#ffffff" };
      default:
        return { bg: colors.gray[600], color: "#ffffff" };
    }
  };

  const formatStatusText = (status) => {
    if (!status) return "N/A";
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Reusable Detail Item Component
  const DetailItem = ({ label, value, isHighlight = false }) => (
    <Box mb={2}>
      <Typography
        variant="caption"
        color={colors.gray[400]}
        fontWeight="600"
        textTransform="uppercase"
        letterSpacing="0.5px"
        display="block"
        mb="4px"
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        color={isHighlight ? colors.blueAccent[500] : colors.gray[100]}
        fontWeight={isHighlight ? "700" : "500"}
        sx={{ wordBreak: "break-word" }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
      keepMounted
      PaperProps={{
        sx: {
          backgroundColor:
            theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
          backgroundImage: "none",
          borderRadius: "16px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
          border: `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.05)"
          }`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 3,
          pb: 2,
          px: { xs: 2, sm: 4 },
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(99, 102, 241, 0.15)"
                  : "rgba(79, 70, 229, 0.1)",
              p: 1,
              borderRadius: "8px",
              display: "flex",
            }}
          >
            <ReceiptLongOutlinedIcon
              sx={{ color: colors.blueAccent[500], fontSize: "28px" }}
            />
          </Box>
          <Box>
            <Typography variant="h3" fontWeight="800" color={colors.gray[100]}>
              Transaction Details
            </Typography>
            <Typography variant="subtitle2" color={colors.gray[400]}>
              Reference ID: #TRX-{localData.id}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: colors.gray[400],
            "&:hover": { color: colors.redAccent[500] },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider
        sx={{
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.08)",
        }}
      />

      <DialogContent sx={{ p: { xs: 2, sm: 4 } }}>
        <Grid container spacing={4}>
          {/* Customer Information */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.02)",
                borderRadius: "12px",
                height: "100%",
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <PersonOutlineOutlinedIcon sx={{ color: colors.gray[300] }} />
                <Typography
                  variant="h5"
                  fontWeight="700"
                  color={colors.gray[100]}
                >
                  Customer Information
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <DetailItem
                    label="Customer Name"
                    value={localData.customerName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DetailItem
                    label="Customer Email"
                    value={localData.customerEmail}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Transaction Date"
                    value={formatDate(localData.date)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem label="Entered By" value={localData.enteredBy} />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Product Information */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.02)",
                borderRadius: "12px",
                height: "100%",
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Inventory2OutlinedIcon sx={{ color: colors.gray[300] }} />
                <Typography
                  variant="h5"
                  fontWeight="700"
                  color={colors.gray[100]}
                >
                  Product Details
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <DetailItem
                    label="Product Title"
                    value={localData.title}
                    isHighlight
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem label="Product Type" value={localData.type} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Quantity Sold"
                    value={localData.quantity?.toLocaleString()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Unit Rate"
                    value={formatCurrency(localData.rate)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Total Amount"
                    value={formatCurrency(localData.totalBalance)}
                    isHighlight
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Financial & Payment Information */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.02)",
                borderRadius: "12px",
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)"
                }`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <AccountBalanceWalletOutlinedIcon
                  sx={{ color: colors.gray[300] }}
                />
                <Typography
                  variant="h5"
                  fontWeight="700"
                  color={colors.gray[100]}
                >
                  Payment & Financials
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="caption"
                    color={colors.gray[400]}
                    fontWeight="600"
                    textTransform="uppercase"
                    display="block"
                    mb="8px"
                  >
                    Payment Status
                  </Typography>
                  <Chip
                    label={formatStatusText(localData.paymentStatus)}
                    sx={{
                      backgroundColor: getStatusColor(localData.paymentStatus)
                        .bg,
                      color: getStatusColor(localData.paymentStatus).color,
                      fontWeight: "bold",
                      borderRadius: "6px",
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <DetailItem
                    label="Amount Paid"
                    value={formatCurrency(localData.amountPaid)}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box mb={2}>
                    <Typography
                      variant="caption"
                      color={colors.gray[400]}
                      fontWeight="600"
                      textTransform="uppercase"
                      display="block"
                      mb="4px"
                    >
                      Outstanding Balance
                    </Typography>
                    <Typography
                      variant="h4"
                      color={
                        Number(localData.outstandingBalance) > 0
                          ? colors.redAccent[500]
                          : colors.greenAccent[500]
                      }
                      fontWeight="800"
                    >
                      {formatCurrency(localData.outstandingBalance)}
                    </Typography>
                  </Box>
                </Grid>

                {/* Installment Plan Details (If applicable) */}
                {localData.paymentStatus === "PARTIAL_PAYMENT" && (
                  <>
                    <Grid item xs={12}>
                      <Divider
                        sx={{
                          borderColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.05)",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DetailItem
                        label="Installment Plan"
                        value={formatStatusText(localData.installmentPlan)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DetailItem
                        label="Payment Frequency"
                        value={formatStatusText(localData.paymentFrequency)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DetailItem
                        label="Duration"
                        value={
                          localData.installmentDuration === "CUSTOM"
                            ? `${localData.customInstallmentDuration} Days`
                            : formatStatusText(localData.installmentDuration)
                        }
                      />
                    </Grid>
                  </>
                )}

                {/* Description Full Width */}
                <Grid item xs={12}>
                  <Divider
                    sx={{
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.05)",
                      mb: 2,
                    }}
                  />
                  <DetailItem
                    label="Sales Notes / Description"
                    value={
                      localData.salesDesc || "No additional notes provided."
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider
        sx={{
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.08)",
        }}
      />

      <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: colors.gray[700],
            color: "#ffffff",
            textTransform: "none",
            fontWeight: "600",
            px: 4,
            py: 1.2,
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: colors.gray[600],
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewMoreDialog;
